/**
 * Forgot password — sends a Supabase reset email. The email links to
 * /reset-password with a recovery token; ResetPassword handles the
 * actual update.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { friendlyError } from '@/lib/utils/friendly-errors';
import { AuthLayout } from '@/components/layout/AuthLayout';

export function ForgotPassword() {
  useEffect(() => { document.title = 'Reset your password — Waypoint'; }, []);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (err) throw err;
      // Always show "sent" message even if email doesn't exist — avoid email enumeration.
      setSent(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <div className="rounded-xl border border-border bg-surface-1 p-8 shadow-glow">
        {sent ? (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-soft text-2xl">&#9993;</div>
            <h1 className="mb-2 text-xl font-medium text-ink">Check your email</h1>
            <p className="text-sm text-ink-muted">
              If <span className="font-mono text-primary-bright">{email}</span> is a registered address, we sent a reset link. Click it to set a new password.
            </p>
            <Link to="/login" className="mt-6 inline-block text-sm text-primary-bright hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="mb-1 text-xl font-medium text-ink">Reset your password</h1>
            <p className="mb-6 text-sm text-ink-muted">
              Enter your email and we'll send you a link to set a new password.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-ink-dim">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-ink outline-hidden focus:border-primary-line"
                />
              </div>
              {error && (
                <div className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                  {error}
                </div>
              )}
              <button type="submit" disabled={submitting || !email} className="btn btn-primary w-full py-2.5">
                {submitting ? 'Sending...' : 'Send reset link'}
              </button>
            </form>
            <p className="mt-6 text-center text-sm text-ink-muted">
              Remember your password? <Link to="/login" className="text-primary-bright hover:underline font-medium">Sign in</Link>
            </p>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
